import { useEffect, useRef, useState } from 'react';
import { ReactComponent as EditIcon } from '../../../assets/tag/icon-edit-small.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/tag/icon-delete-small.svg';
import { SYSTEM_TAGS, TAG_COLORS } from './tagsConfig';
import { reilmAPI } from '../../../services/apiClient';

const DEFAULT_COLOR = TAG_COLORS[9]; // blue

const colorUsageCount = (color, customTags) =>
  customTags.filter(t => t.color === color).length;

const ColorGrid = ({ selected, onChange }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 28px)',
      gap: 8,
      marginTop: 10,
      justifyContent:'space-around'
    }}
  >
    {TAG_COLORS.map(c => (
      <button
        key={c}
        onClick={() => onChange(c)}
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: c,
          border: selected === c ? '2.5px solid white' : '2.5px solid transparent',
          boxShadow: selected === c ? `0 0 0 2px ${c}` : 'none',
          cursor: 'pointer',
          outline: 'none',
          padding: 0,
        }}
      />
    ))}
  </div>
);

const ManageTagsModal = ({
  open,
  onClose,
  customTags,
  tagCounts,
  onTagCreated,
  onTagUpdated,
  onTagDeleted,
}) => {
  const [activeTab, setActiveTab] = useState('manage');
  const [activeToggle, setActiveToggle] = useState('user');
  const [editingTag, setEditingTag] = useState(null);
  const [formName, setFormName] = useState('');
  const [formColor, setFormColor] = useState(DEFAULT_COLOR);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setActiveTab('manage');
      setActiveToggle('user');
      setEditingTag(null);
      setFormName('');
      setFormColor(DEFAULT_COLOR);
    }
  }, [open]);

  if (!open) return null;

  const goToAdd = () => {
    setEditingTag(null);
    setFormName('');
    setFormColor(DEFAULT_COLOR);
    setActiveTab('add');
  };

  const goToEdit = (tag) => {
    setEditingTag(tag);
    setFormName(tag.name);
    setFormColor(tag.color ?? DEFAULT_COLOR);
    setActiveTab('edit');
  };

  const goToManage = () => {
    setActiveTab('manage');
    setEditingTag(null);
    setFormName('');
    setFormColor(DEFAULT_COLOR);
  };

  const handleSave = async () => {
    const name = formName.trim();
    if (!name) return;
    setSaving(true);
    try {
      if (activeTab === 'edit' && editingTag) {
        const { data } = await reilmAPI.updateTag(editingTag._id, { name, color: formColor });
        onTagUpdated(data.data.tag);
      } else {
        const { data } = await reilmAPI.createTag({ name, color: formColor });
        onTagCreated(data.data.tag);
      }
      goToManage();
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await reilmAPI.deleteTag(id);
      onTagDeleted(id);
    } catch {
      // silent
    } finally {
      setDeletingId(null);
    }
  };

  const countFor = (value) => tagCounts[value] ?? 0;
  const colorUsed = colorUsageCount(formColor, customTags);

  const tabLabel = activeTab === 'edit' ? 'Edit Tag' : 'Add Tag';

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/30"
      onMouseDown={(e) => { if (e.target === backdropRef.current) onClose(); }}
    >
      <div
        style={{ background: '#F8FEF4', width: 480, borderRadius: 16, maxHeight: '80vh' }}
        className="flex flex-col shadow-xl overflow-hidden"
      >
        {/* Tabs header */}
        <div className="flex items-center justify-between px-[28px] pt-[22px] pb-[0px]">
          <div className="flex items-end gap-x-[24px]">
            {/* Manage Tags tab */}
            <button
              onClick={goToManage}
              style={{
                paddingBottom: 10,
                borderBottom: activeTab === 'manage' ? '2px solid #4CBC18' : '2px solid transparent',
                fontWeight: activeTab === 'manage' ? 700 : 400,
                color: activeTab === 'manage' ? '#17270D' : '#A7C199',
                fontSize: 15,
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'manage' ? '2px solid #4CBC18' : '2px solid transparent',
                cursor: 'pointer',
                outline: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Manage Tags
            </button>

            {/* Add Tag / Edit Tag tab */}
            <button
              onClick={activeTab === 'manage' ? goToAdd : undefined}
              style={{
                paddingBottom: 10,
                borderBottom: activeTab !== 'manage' ? '2px solid #4CBC18' : '2px solid transparent',
                fontWeight: activeTab !== 'manage' ? 700 : 400,
                color: activeTab !== 'manage' ? '#17270D' : '#A7C199',
                fontSize: 15,
                background: 'none',
                border: 'none',
                borderBottom: activeTab !== 'manage' ? '2px solid #4CBC18' : '2px solid transparent',
                cursor: 'pointer',
                outline: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {tabLabel}
            </button>
          </div>

          <button
            onClick={onClose}
            className="w-[28px] h-[28px] rounded-full flex items-center justify-center text-brand-unselectedGray hover:bg-brand-grayGreen transition-colors text-[20px] leading-none mb-[8px]"
          >
            ×
          </button>
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: '#DAE4D5', margin: '0 0 0 0' }} />

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-[28px] py-[20px]">

          {/* ── MANAGE TAB ── */}
          {activeTab === 'manage' && (
            <>
              {/* User / System toggle */}
              <div
                style={{
                  display: 'inline-flex',
                  border: '1.5px solid #DAE4D5',
                  borderRadius: 999,
                  padding: 3,
                  marginBottom: 20,
                }}
              >
                {['user', 'system'].map((key) => (
                  <button
                    key={key}
                    onClick={() => setActiveToggle(key)}
                    style={{
                      borderRadius: 999,
                      padding: '5px 18px',
                      fontSize: 13,
                      fontWeight: 600,
                      background: activeToggle === key ? '#4CBC18' : 'transparent',
                      color: activeToggle === key ? '#fff' : '#17270D',
                      border: 'none',
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'background 0.15s',
                    }}
                  >
                    {key === 'user' ? 'User Tags' : 'System Tags'}
                  </button>
                ))}
              </div>

              {/* List header */}
              <div
                style={{ display: 'grid', gridTemplateColumns: '1fr 60px 56px', paddingBottom: 6, borderBottom: '1px solid #DAE4D5' }}
              >
                <span className="text-[12px] font-semibold text-brand-unselectedGray uppercase tracking-wide">Tags</span>
                <span className="text-[12px] font-semibold text-brand-unselectedGray uppercase tracking-wide text-right">Count</span>
                <span />
              </div>

              {/* Tag rows */}
              <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                {activeToggle === 'user' && (
                  <>
                    {customTags.length === 0 && (
                      <p className="text-[13px] text-brand-unselectedGray py-[16px]">No custom tags yet. Click "Add Tag" to create one.</p>
                    )}
                    {customTags.map(tag => (
                      <div
                        key={tag._id}
                        style={{ display: 'grid', gridTemplateColumns: '1fr 60px 56px', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(218,228,213,0.5)' }}
                      >
                        <div className="flex items-center gap-x-[10px]">
                          <span style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: tag.color ?? '#DAE4D5', flexShrink: 0 }} />
                          <span className="text-[13px] font-medium text-brand-dark">{tag.name}</span>
                        </div>
                        <span className="text-[13px] text-brand-unselectedGray text-right">{countFor(tag._id)}</span>
                        <div className="flex items-center justify-end gap-x-[8px]">
                          <button
                            onClick={() => goToEdit(tag)}
                            className="flex items-center justify-center w-[20px] h-[20px] hover:opacity-70 transition-opacity"
                          >
                            <EditIcon />
                          </button>
                          <button
                            onClick={() => handleDelete(tag._id)}
                            disabled={deletingId === tag._id}
                            className="flex items-center justify-center w-[20px] h-[20px] hover:opacity-70 transition-opacity disabled:opacity-30"
                          >
                            <DeleteIcon />
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {activeToggle === 'system' && (
                  <>
                    {SYSTEM_TAGS.map(tag => (
                      <div
                        key={tag.value}
                        style={{ display: 'grid', gridTemplateColumns: '1fr 60px 56px', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(218,228,213,0.5)' }}
                      >
                        <div className="flex items-center gap-x-[10px]">
                          <span style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: tag.color, flexShrink: 0 }} />
                          <span className="text-[13px] font-medium text-brand-dark">{tag.label}</span>
                        </div>
                        <span className="text-[13px] text-brand-unselectedGray text-right">{countFor(tag.value)}</span>
                        <span />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </>
          )}

          {/* ── ADD / EDIT TAB ── */}
          {(activeTab === 'add' || activeTab === 'edit') && (
            <div className="flex flex-col gap-y-[20px]">
              {/* Name */}
              <div>
                <label className="block text-[13px] font-semibold text-brand-dark mb-[8px]">Name</label>
                <input
                  autoFocus
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
                  className="w-full text-[14px] border border-brand-muted rounded-[10px] px-[14px] py-[10px] bg-white outline-none focus:border-brand-green"
                  placeholder="Tag name…"
                />
              </div>

              {/* Color */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-[13px] font-semibold text-brand-dark">Color</label>
                  {colorUsed > 0 && (
                    <span className="text-[12px] text-brand-unselectedGray">
                      Selected color has been used {colorUsed} {colorUsed === 1 ? 'time' : 'times'}.
                    </span>
                  )}
                </div>
                <ColorGrid selected={formColor} onChange={setFormColor} />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-x-[10px] pt-[4px]">
                <button
                  onClick={goToManage}
                  style={{ border: '1.5px solid #DAE4D5', borderRadius: 999, padding: '8px 22px', fontSize: 13, fontWeight: 500, background: 'white', cursor: 'pointer', color: '#17270D' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !formName.trim()}
                  style={{ borderRadius: 999, padding: '8px 22px', fontSize: 13, fontWeight: 600, background: '#4CBC18', color: 'white', border: 'none', cursor: 'pointer', opacity: saving || !formName.trim() ? 0.5 : 1 }}
                >
                  {saving ? '…' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageTagsModal;
